/**
 * Slug helper for SEO-05 — converts artifact names into URL-friendly slugs
 * and parses slug-encoded short ids back out.
 *
 * Format: `<translit-name>-<full-21-char-nanoid-id>`
 *
 * Why `@sindresorhus/slugify`:
 * - Best Russian transliteration of the candidates (BGN-style: `Кофейня` → `kofeynya`)
 * - Pure ESM, two zero-dep transitive deps, esbuild-bundleable into the worker
 * - Supports custom replacements + locale-aware lowercasing
 *
 * See research/seo-05-slug-urls/A-libraries.md for full comparison.
 *
 * Anti-pattern note (.claude/rules/anti-patterns.md "Module-top-level const"):
 * `MAX_SLUG_LEN` and `RU_CUSTOM_REPLACEMENTS` are pure data (a number constant
 * and a static array of string pairs), never used as control-flow boundaries
 * inside loops with circular-init risk. Safe at top-level.
 */
import sindreSlugify from "@sindresorhus/slugify";

/** SEO sweet spot — Google truncates URLs around 70-75 chars in SERP. */
const MAX_SLUG_LEN = 60;

/** nanoid v5.1.7 default length — verified live against niches/business_plans/brand_kits in research B. */
const SHORT_ID_LEN = 21;

/** nanoid v5.1.7 default alphabet — used to validate parsed short ids. */
const NANOID_ALPHABET_RE = /^[A-Za-z0-9_-]+$/;

/**
 * Custom replacements applied BEFORE transliteration.
 * Note: `&` → ` and ` is already a built-in default in @sindresorhus/slugify; do not duplicate.
 */
const RU_CUSTOM_REPLACEMENTS: Array<[string, string]> = [
  ["№", "no"],
  ["%", " procent "],
  ["+", " plus "],
  ["@", " at "],
];

/**
 * Convert an arbitrary input string into a clean URL slug part (no id suffix).
 * Returns `""` for empty / whitespace / emoji-only / special-only inputs —
 * caller should fall back to bare id in that case (see `buildSlug`).
 */
export function toSlug(input: string, opts?: { maxLen?: number }): string {
  const maxLen = opts?.maxLen ?? MAX_SLUG_LEN;

  const raw = sindreSlugify(input, {
    decamelize: false, // keeps "iPhone" intact, doesn't split on case
    customReplacements: RU_CUSTOM_REPLACEMENTS,
    locale: "ru", // makes lowercase use toLocaleLowerCase("ru")
  });

  if (!raw) return "";
  if (raw.length <= maxLen) return raw;

  // Truncate, then back off to the previous "-" to avoid mid-word cuts.
  const sliced = raw.slice(0, maxLen);
  const lastDash = sliced.lastIndexOf("-");
  return lastDash > maxLen / 2 ? sliced.slice(0, lastDash) : sliced;
}

/**
 * Extract the trailing 21-char nanoid from a full slug like
 * `kofeynya-spetsializirovannaya-q7TOgkie0dTpWOxGPKLYF`.
 *
 * Uses fixed-length tail slice (NOT `split('-').pop()`) because the nanoid
 * alphabet includes `-` as a valid character — splitting can chop the id.
 * Returns null if the trailing slice doesn't match nanoid alphabet.
 */
export function parseShortId(slug: string): string | null {
  if (slug.length < SHORT_ID_LEN) return null;
  const tail = slug.slice(-SHORT_ID_LEN);
  if (!NANOID_ALPHABET_RE.test(tail)) return null;
  return tail;
}

/**
 * True if the slug is a bare legacy nanoid (no name prefix at all):
 * exactly 21 chars long AND fully matches the nanoid alphabet.
 *
 * Note: nanoid alphabet includes `-`, so "contains a dash" is NOT a
 * disqualifier here — only length and alphabet matter.
 */
export function isBareLegacyId(slug: string): boolean {
  return slug.length === SHORT_ID_LEN && NANOID_ALPHABET_RE.test(slug);
}

/**
 * Build the canonical slug for a row. If the name slugifies to empty
 * (whitespace / emoji / special-only), falls back to bare id — the
 * page route's self-healing redirect will then keep the URL stable.
 */
export function buildSlug(name: string | null | undefined, id: string): string {
  const namePart = name ? toSlug(name) : "";
  return namePart ? `${namePart}-${id}` : id;
}
